import { OAuth2AuthCodePKCE } from '@main/oauth2-auth-code-pkce';
import { SignInResponse } from '@nhost/nhost-js';
import { createListenerMiddleware, TypedStartListening } from '@reduxjs/toolkit';

import { router } from '../../router';
import { BackofficeAppDispatch, BackofficeRootState } from '../../store';
import { nhost } from '../../utils/nhost-client';
import {
  errorOnLoadingSSOUser,
  getUserSSOState,
  samlSSOInitiated,
  signInPageLoaded,
} from './slice';

export const userListenerMiddleware = createListenerMiddleware();

const startListening = userListenerMiddleware.startListening as TypedStartListening<
  BackofficeRootState,
  BackofficeAppDispatch
>;

const oauthClient = new OAuth2AuthCodePKCE({
  authorizationUrl: `${import.meta.env.VITE_NHOST_AUTH_URL}/saml`,
  tokenUrl: `${import.meta.env.VITE_NHOST_AUTH_URL}/saml/token`,
  clientId: 'dummy',
  redirectUrl: `${window.location.origin}`,
  scopes: [],
  onAccessTokenExpiry(refreshAccessToken) {
    return refreshAccessToken();
  },
  onInvalidGrant(refreshAuthCodeOrRefreshToken) {
    return refreshAuthCodeOrRefreshToken();
  },
});

startListening({
  actionCreator: signInPageLoaded,
  effect: async (_, { take, fork, dispatch, cancelActiveListeners, getState }) => {
    cancelActiveListeners();

    fork(async () => {
      try {
        if (getUserSSOState(getState()).error) {
          return;
        }

        const hasAuthCode = await oauthClient.isReturningFromAuthServer();

        if (!hasAuthCode) {
          return;
        }
        const tokenResult = await oauthClient.getAccessToken();

        const res = await fetch(`${import.meta.env.VITE_NHOST_AUTH_URL}/saml/identify`, {
          method: 'POST',
          body: JSON.stringify({
            access_token: tokenResult.token?.value,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const result = (await res.json()) as SignInResponse & { message: string };

        if (!res.ok) {
          throw new Error(result?.message);
        }

        const sessionResult = await nhost.auth.refreshSession(
          result?.session?.refreshToken ?? undefined,
        );

        if (sessionResult.error?.message) {
          throw new Error(sessionResult.error.message);
        }

        const isAdmin = sessionResult.session?.user.roles.includes('admin');

        if (!isAdmin) {
          throw new Error('User is not an admin');
        }

        router.navigate({ to: '/organizations' });
      } catch (e) {
        if (e instanceof Error) {
          dispatch(
            errorOnLoadingSSOUser({
              error: e.message,
            }),
          );
        } else {
          dispatch(errorOnLoadingSSOUser());
        }
      } finally {
        oauthClient.reset();
      }
    });

    await take(samlSSOInitiated.match);

    await oauthClient.fetchAuthorizationCode({
      tenant: import.meta.env.VITE_APP_ENV === 'local' ? 'example.com' : 'complyance.com',
    });
  },
});
